/**
 * BpDiaryCard — дневник на кръвното налягане със графика и средно за седмицата.
 * Данни: GET/POST /api/bp/log
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { HeartPulse, Plus, Trash2, TrendingDown, TrendingUp } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  CartesianGrid, ReferenceLine,
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const V = {
  ok: { color: "#10B981", label: "В ЦЕЛТА" },
  above: { color: "#F59E0B", label: "НАД ЦЕЛТА" },
  high: { color: "#EF4444", label: "ВИСОКО" },
  low: { color: "#0EA5E9", label: "НИСКО" },
  unknown: { color: "#94A3B8", label: "НЯМА ДАННИ" },
};

export default function BpDiaryCard({ cardCls, isDark }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ s: "", d: "", p: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/bp/log?days=30`);
      setData(data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.s || !form.d) return;
    setSaving(true);
    try {
      await axios.post(`${API}/bp/log`, {
        systolic: Number(form.s),
        diastolic: Number(form.d),
        pulse: form.p ? Number(form.p) : null,
      });
      setForm({ s: "", d: "", p: "" });
      await load();
      toast.success("Измерването е записано");
    } catch {
      toast.error("Записването не успя");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/bp/log/${id}`);
      await load();
    } catch {
      toast.error("Изтриването не успя");
    }
  };

  const week = data?.week;
  const v = V[week?.verdict] || V.unknown;
  const dailySeries = (data?.daily || []).map((d) => ({
    ...d, label: d.date?.slice(5).replace("-", "."),
  }));
  const chart = dailySeries.length > 1
    ? dailySeries
    : [...(data?.entries || [])]
        .sort((a, b) => (a.time || "").localeCompare(b.time || ""))
        .map((e, i, arr) => ({
          ...e,
          label: arr.filter((x, j) => j < i && x.time === e.time).length
            ? `${e.time} (${arr.filter((x, j) => j <= i && x.time === e.time).length})`
            : e.time,
        }));
  const inputCls = `w-16 px-2 py-1.5 rounded-lg text-[13px] border ${
    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`;

  return (
    <div className={`p-4 rounded-2xl border ${cardCls}`} data-testid="bp-diary-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <HeartPulse className="w-4 h-4 text-rose-500" />
            Дневник на кръвното
          </h3>
          <p className="text-[11px] opacity-60 mt-0.5">{data?.target_bg}</p>
        </div>
        <span className="text-[9px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: v.color + "22", color: v.color }} data-testid="bp-week-verdict">
          {v.label}
        </span>
      </div>

      {/* въвеждане */}
      <div className="flex items-end gap-1.5 mt-3 flex-wrap">
        <div>
          <div className="text-[9px] opacity-60 mb-0.5">горна</div>
          <input type="number" value={form.s} placeholder="128" className={inputCls}
            onChange={(e) => setForm((f) => ({ ...f, s: e.target.value }))}
            data-testid="bp-input-systolic" />
        </div>
        <span className="opacity-40 pb-2">/</span>
        <div>
          <div className="text-[9px] opacity-60 mb-0.5">долна</div>
          <input type="number" value={form.d} placeholder="78" className={inputCls}
            onChange={(e) => setForm((f) => ({ ...f, d: e.target.value }))}
            data-testid="bp-input-diastolic" />
        </div>
        <div>
          <div className="text-[9px] opacity-60 mb-0.5">пулс</div>
          <input type="number" value={form.p} placeholder="68" className={inputCls}
            onChange={(e) => setForm((f) => ({ ...f, p: e.target.value }))}
            data-testid="bp-input-pulse" />
        </div>
        <button onClick={save} disabled={saving || !form.s || !form.d}
          className="text-[12px] font-bold px-3 py-2 rounded-xl bg-rose-500 text-white
                     hover:bg-rose-600 active:scale-[0.98] transition-colors
                     disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="bp-save-btn">
          <Plus className="w-3.5 h-3.5" /> {saving ? "Записвам…" : "Запиши"}
        </button>
      </div>

      {/* средно за седмицата */}
      {week?.count ? (
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}
            data-testid="bp-week-avg">
            <div className="text-[9px] opacity-60">средно за 7 дни</div>
            <div className="text-lg font-bold" style={{ color: v.color }}>
              {week.avg_systolic}/{week.avg_diastolic}
            </div>
            <div className="text-[9px] opacity-50">{week.count} измервания</div>
          </div>
          <div className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
            <div className="text-[9px] opacity-60">в целта</div>
            <div className="text-lg font-bold">{week.in_target_pct}%</div>
            <div className="text-[9px] opacity-50">{week.in_target} от {week.count}</div>
          </div>
          <div className={`rounded-xl p-2.5 ${isDark ? "bg-white/5" : "bg-slate-50"}`}>
            <div className="text-[9px] opacity-60">пулс</div>
            <div className="text-lg font-bold">{week.avg_pulse ?? "—"}</div>
            <div className="text-[9px] opacity-50">средно за 7 дни</div>
          </div>
        </div>
      ) : (
        <div className="text-[11px] opacity-60 mt-3" data-testid="bp-empty">
          Още няма измервания. Запишете първото — след 7 дни ще видите средната стойност.
        </div>
      )}

      {data?.trend && (
        <div className="text-[11px] mt-2 flex items-center gap-1.5"
          style={{ color: data.trend.delta_systolic <= 0 ? "#10B981" : "#EF4444" }}
          data-testid="bp-trend">
          {data.trend.delta_systolic <= 0
            ? <TrendingDown className="w-3.5 h-3.5" />
            : <TrendingUp className="w-3.5 h-3.5" />}
          {data.trend.text_bg}
        </div>
      )}

      {/* графика */}
      {chart.length > 1 && (
        <div className="h-40 mt-3" data-testid="bp-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 5, right: 8, left: -22, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1e293b" : "#e2e8f0"} />
              <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="#94a3b8" />
              <YAxis domain={[50, 180]} tick={{ fontSize: 9 }} stroke="#94a3b8" />
              <RTooltip contentStyle={{
                fontSize: 11, borderRadius: 10,
                background: isDark ? "#0f172a" : "#fff",
                border: `1px solid ${isDark ? "#1e293b" : "#e2e8f0"}`,
              }} />
              <ReferenceLine y={130} stroke="#F59E0B" strokeDasharray="4 4" />
              <ReferenceLine y={80} stroke="#F59E0B" strokeDasharray="4 4" />
              <Line type="monotone" dataKey="systolic" name="горна" stroke="#EF4444"
                strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="diastolic" name="долна" stroke="#0EA5E9"
                strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* последни записи */}
      {!!data?.entries?.length && (
        <div className="mt-3 space-y-1" data-testid="bp-entries">
          {[...data.entries].reverse().slice(0, 5).map((e) => (
            <div key={e.id} className="flex items-center justify-between text-[11px]
                                       py-1 border-b border-dashed border-slate-500/20">
              <span className="opacity-60">{e.date} · {e.time}</span>
              <span className="font-semibold">
                {e.systolic}/{e.diastolic}{e.pulse ? ` · пулс ${e.pulse}` : ""}
              </span>
              <button onClick={() => remove(e.id)} className="opacity-40 hover:opacity-100"
                data-testid={`bp-delete-${e.id}`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-[9px] opacity-50 mt-2">{data?.how_bg}</div>
    </div>
  );
}
